/*
 * Grow-only dynamic widget height: device gate, activation logging and the
 * monotonic height clamp helpers.
 */

const LOW_END_DEVICE_MEMORY_MAX = 4; // deviceMemory <= 4 => <=4GB budget/entry tier (flagships report 8)
const LOW_END_CORE_COUNT_MAX = 4; // hardwareConcurrency < 4 => ancient-device safety-net

const ACTIVATION_LOG_MESSAGES = {
  lowEndAndroid:
    'MVF INFO LOG - Low-end Android device detected (navigator.deviceMemory <= 4 i.e. <= 4GB budget/entry tier, and/or navigator.hardwareConcurrency < 4 logical cores; coarse proxies for constrained rendering headroom, not a direct GPU or animation measurement). Frame drops during height transitions can cause layout shift, so activating grow-only dynamic widget height for this session.',
  reducedMotion:
    'MVF INFO LOG - Reduced-motion preference detected (prefers-reduced-motion: reduce). Activating grow-only dynamic widget height to minimise on-screen movement and layout shift for this session.',
  testOverride:
    'MVF INFO LOG - Grow-only dynamic widget height activated via test override (?forceGrowOnlyDynamicHeight=1).',
};

let cachedDecision;

const isAndroidUserAgent = () =>
  typeof navigator !== 'undefined' &&
  navigator.userAgent.toLowerCase().indexOf('android') > -1;

const prefersReducedMotion = () => {
  if (
    typeof window === 'undefined' ||
    typeof window.matchMedia !== 'function'
  ) {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

const hasForceGrowOnlyQueryParam = () => {
  if (typeof window === 'undefined') return false;
  return (
    new URLSearchParams(window.location.search).get(
      'forceGrowOnlyDynamicHeight'
    ) === '1'
  );
};

const isLowEndAndroid = () => {
  if (!isAndroidUserAgent()) return false;
  const { deviceMemory, hardwareConcurrency } = navigator;
  const hasLowMemory =
    typeof deviceMemory === 'number' &&
    deviceMemory <= LOW_END_DEVICE_MEMORY_MAX;
  const hasFewCores =
    typeof hardwareConcurrency === 'number' &&
    hardwareConcurrency < LOW_END_CORE_COUNT_MAX;
  return hasLowMemory || hasFewCores;
};

const logActivation = (reason) => {
  // eslint-disable-next-line no-console
  console.log(ACTIVATION_LOG_MESSAGES[reason]);
};

/**
 * Decides whether grow-only dynamic widget height is active for this session.
 * Pure/synchronous so it can seed window.__private__ before Redux hydrates.
 * Memoised: decides and logs exactly once per page load.
 * @returns {{ active: boolean, reason: ('testOverride'|'reducedMotion'|'lowEndAndroid'|undefined) }}
 */
export const shouldActivateGrowOnly = () => {
  if (cachedDecision) return cachedDecision;

  let reason;
  if (hasForceGrowOnlyQueryParam()) reason = 'testOverride';
  else if (prefersReducedMotion()) reason = 'reducedMotion';
  else if (isLowEndAndroid()) reason = 'lowEndAndroid';

  if (reason) logActivation(reason);
  cachedDecision = { active: Boolean(reason), reason };
  return cachedDecision;
};

/** Test-only: clears the memoised decision so each test decides afresh. */
export const __resetGrowOnlyDecisionCache = () => {
  cachedDecision = undefined;
};

const getHighWaterMark = () =>
  (typeof window !== 'undefined' &&
    window.__private__?.dynamicWidgetHeightData?.growOnlyHighWaterMark) ||
  0;

/**
 * Read-only grow-only clamp. When grow-only is active, raises the requested
 * height to the session high-water mark so height never shrinks; otherwise
 * returns the requested height unchanged. Non-numeric input is passed through
 * untouched so bad values fall back to the pre-grow-only behaviour rather than
 * becoming NaN.
 * @param {number} requestedHeight - height in px
 * @returns {number}
 */
export const getGrowOnlyClampedHeight = (requestedHeight) => {
  if (!shouldActivateGrowOnly().active) return requestedHeight;
  const height = Number(requestedHeight);
  if (!Number.isFinite(height)) return requestedHeight;
  return Math.max(height, getHighWaterMark());
};

/**
 * Raises the session high-water mark to `appliedHeight` when it is larger.
 * No-op when grow-only is inactive or window.__private__ is unavailable.
 * Mutates window.__private__ directly (fast-access store; no re-render).
 * @param {number} appliedHeight - height in px that has just been applied
 */
export const recordGrowOnlyHeight = (appliedHeight) => {
  if (!shouldActivateGrowOnly().active) return;
  const data =
    typeof window !== 'undefined' &&
    window.__private__?.dynamicWidgetHeightData;
  if (!data) return;
  const height = Number(appliedHeight);
  if (!Number.isFinite(height)) return;
  if (height > (data.growOnlyHighWaterMark || 0)) {
    data.growOnlyHighWaterMark = height;
  }
};
